/**
 * Custom time submission helper functions
 * Handles submission of manual time entries
 */

import moment from "moment-timezone";
import axios from "axios";
import { formatTime, covertTimeSatmp } from "../services/trackerServices";
import { resetManualTimeState } from "./manualTimeHelpers";
import { toastConfig } from "./constants";
import { loggerDayKey, getLoggerZone } from "../../../../../utils/tenantDate";

/**
 * Creates custom time submission handler
 * @param {Object} dependencies - Object containing all necessary dependencies
 */
export const createCustomTimeSubmissionHandler = ({
  // State values
  taskName,
  projectName,
  projectId,
  date,
  client,
  clientId,
  sprintId,
  tagId,
  isBillable,
  selectedUser,
  startTimeManual,
  startTimeManual24,
  endTimeManual,
  endTimeManual24,
  uFormatedTimeManual,
  uDuration,
  splitted,
  splittedDate,
  eventDetails,
  appSettings,
  // Refs
  startInputRef,
  endInputRef,
  start12HrTimeInputRef,
  end12HrTimeInputRef,
  // Setters
  setIsTimeInputActive,
  setTaskError,
  setUDuration,
  setUFormatedTimeManual,
  setIsLoading,
  setIsCustomeTimeLoading,
  setIsRunning,
  setCustomeTime,
  setTag,
  setTagId,
  setDate,
  setProjectName,
  setProjectId,
  setTaskName,
  setStopRunning,
  setTimerUpdate,
  setSprintName,
  setSprintId,
  setStartTimeManual,
  setEndTimeManual,
  setStartTimeManual24,
  setEndTimeManual24,
  setSplitted,
  setSplittedDate,
  setUManualStartTimeError,
  setUManualEndTimeError,
  setUManualFormatedTimeError,
  // Functions
  dispatch,
  eventDetailReset,
  setTimerData,
  token,
  toast,
}) => {
  /**
   * Handle custom time submission
   */
  const handleSetCustomeTimes = () => {
    try {
      if (taskName && projectName && projectId && date && client && clientId && sprintId) {
        try {
          setIsTimeInputActive(false);
          const latestStartTime = appSettings.timeFormat === 24
            ? startInputRef.current?.value || startTimeManual24
            : start12HrTimeInputRef.current?.value || startTimeManual;
          const latestEndTime = appSettings.timeFormat === 24
            ? endInputRef.current?.value || endTimeManual24
            : end12HrTimeInputRef.current?.value || endTimeManual;

          // `date` is the day chosen in the picker — a CALENDAR DATE, not an
          // instant. Formatting it in the tenant zone rolled it forward a day
          // whenever the user's local time was far enough behind (logging at
          // 22:32 in Mexico became the 9th on an IST tenant). Keep the day
          // exactly as picked; the TIME below is what gets interpreted in the
          // tenant zone.
          const date1 = moment(date).format("YYYY-MM-DD HH:mm:ss");

          const format = appSettings.timeFormat === 24 ? "HH:mm" : "h:mm A";
          const time1 = moment(latestStartTime, format).tz(global.config.getTimeZone());
          const time2 = moment(latestEndTime, format).tz(global.config.getTimeZone());

          // Validate that times are valid
          if (!time1.isValid() || !time2.isValid()) {
            setTaskError(true);
            toast.error("Invalid start or end time format.", toastConfig);
            return;
          }

          // Skip time comparison validation if time is split across multiple days
          // For split times, end time on first day can be before/equal to start time
          let timeDifferenceInSeconds;
          // Check if splittedDate is an object with day1 and day2 properties (split time structure)
          const isSplitTime = splitted && splittedDate && (
            (Array.isArray(splittedDate) && splittedDate.length > 0) ||
            (typeof splittedDate === 'object' && splittedDate.day1 && splittedDate.day2)
          );

          if (isSplitTime) {
            // For split times, calculate duration from the formatted time string
            // uFormatedTimeManual should contain the total duration (e.g., "25:30:00")
            if (uFormatedTimeManual) {
              const timeParts = uFormatedTimeManual.split(":").map(Number);
              if (timeParts.length === 3 && !isNaN(timeParts[0]) && !isNaN(timeParts[1]) && !isNaN(timeParts[2])) {
                timeDifferenceInSeconds = timeParts[0] * 3600 + timeParts[1] * 60 + timeParts[2];
                setUDuration(parseInt(timeDifferenceInSeconds));
              } else {
                setTaskError(true);
                toast.error("Invalid duration format for split time.", toastConfig);
                return;
              }
            } else {
              setTaskError(true);
              toast.error("Duration not set for split time.", toastConfig);
              return;
            }
          } else {
            // For single day times, validate and calculate duration
            if (time2.diff(time1, 'seconds') <= 0) {
              setTaskError(true);
              toast.error("Starting time and ending time shouldn't be the same or end time must be after start time.", toastConfig);
              return;
            }
            timeDifferenceInSeconds = time2.diff(time1, "seconds");
            setUDuration(parseInt(timeDifferenceInSeconds));
            setUFormatedTimeManual(
              `${formatTime(Math.floor(timeDifferenceInSeconds / 3600))}:${formatTime(
                Math.floor((timeDifferenceInSeconds % 3600) / 60)
              )}:${formatTime(timeDifferenceInSeconds % 60)}`
            );
          }

          let postData;
          try {
            postData = {
              task: taskName,
              project: projectId,
              date: date1,
              tag: tagId,
              hr: parseInt(Math.floor(uDuration / 3600)),
              min: parseInt(Math.floor(uDuration % 3600) / 60),
              sec: parseInt(Math.floor(uDuration % 60)),
              duration: uDuration,
              startTime: covertTimeSatmp(date1, startTimeManual && startTimeManual.trim()),
              endTime: covertTimeSatmp(date1, endTimeManual && endTimeManual.trim()),
              user: selectedUser,
              client: client,
              clientId: clientId,
              billable: isBillable,
              splittedDate: splittedDate,
              // Typed times are read back in the zone the entry is FILED under,
              // which is the tenant's. Sending the browser's zone here would
              // park the instant and the stored localDate in two different
              // zones, so the row's clock and its day heading would disagree.
              // localZone below is only a fallback for tenants with no zone set.
              tz: global.config.getTimeZone(),
              // The day as the logger sees it, sent as data rather than
              // re-derived server-side from an instant.
              localDate: loggerDayKey(date),
              localZone: getLoggerZone(),
            };

            if (sprintId) {
              postData.sprint = sprintId;
            }
          } catch (err) {
            console.error("Error building postData:", err);
            return;
          }

          setIsLoading(true);
          setIsCustomeTimeLoading(true);

          // Use importData endpoint if eventDetails exists (calendar event flow)
          // Otherwise use regular endpoint
          const apiEndpoint = eventDetails?.title && eventDetails?.start && eventDetails?.end
            ? "/api/time-track/importdata/custome-time"
            : "/api/time-track/custome-time-set";

          axios
            .post(apiEndpoint, postData, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
            .then(async (response) => {
              try {
                await dispatch(setTimerData(Date.now()));
                setIsLoading(false);
                setIsRunning(false);
                setCustomeTime(true);
                setTag(null);
                setTagId(null);
                setDate(new Date());
                setProjectName(null);
                setProjectId(null);
                setTaskName("");
                setStopRunning((prevState) => !prevState);
                setTimerUpdate((prevState) => !prevState);
                setSprintName("");
                setSprintId("");
                resetManualTimeState({
                  setStartTimeManual,
                  setEndTimeManual,
                  setStartTimeManual24,
                  setEndTimeManual24,
                  setUFormatedTimeManual,
                  setSplitted,
                  setSplittedDate,
                  setUManualStartTimeError,
                  setUManualEndTimeError,
                  setUManualFormatedTimeError,
                }, true); // Use 30 minute duration
                setDate(new Date());
                setIsCustomeTimeLoading(false);
                dispatch(eventDetailReset());

                toast("Time added Successfully", toastConfig);
              } catch (innerErr) {
                console.error("Error in response handling:", innerErr);
              }
            })
            .catch((error) => {
              console.error("API Error:", error);
              global.config.slackMessage(error.toString());
            });

          setTaskError(false);
        } catch (err) {
          console.error("Internal block error:", err);
        }
      } else {
        try {
          setTaskError(true);
          let errors = [];

          if (!taskName) errors.push("'Task'");
          if (!projectName || !projectId) errors.push("'Project'");
          if (!date) errors.push("'Date'");
          if (!client || !clientId) errors.push("'Client'");
          if (!sprintId) errors.push("Sprint");

          if (errors.length > 0) {
            toast(`Please select ${errors.join(", ")}`, toastConfig);
          }
        } catch (validationErr) {
          console.error("Validation Error:", validationErr);
        }
      }
    } catch (error) {
      console.error("Unexpected Error in handleSetCustomeTimes:", error);
    }
  };

  return {
    handleSetCustomeTimes,
  };
};
